import * as React from 'react';
import { IEmployee } from 'typings/employee';
import { DeleteUserModal } from 'nomst/Components/Configuration/deleteUserModal';
import './deletedEmployees.scss';

interface IDeletedEmployeesProps {
    employees: IEmployee[];
    deleteEmployeesCall: (employees: IEmployee[], removeUsers: boolean) => Promise<void>;
}

interface IDeletedEmployeesState {
    showDeleteModal: boolean;
    isProcessing: boolean;
}

export default class DeletedEmployees extends React.Component<IDeletedEmployeesProps, IDeletedEmployeesState> {
    private userCount: number = 0;

    constructor(props: IDeletedEmployeesProps) {
        super(props);

        this.handleOpenDeleteUserModal = this.handleOpenDeleteUserModal.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleDeleteEmployeesClick = this.handleDeleteEmployeesClick.bind(this);
        this.handleDeleteEmployeesKeepUsersClick = this.handleDeleteEmployeesKeepUsersClick.bind(this);

        this.userCount = props.employees.filter(ee => ee.isUser).length;
        this.state = { showDeleteModal: false, isProcessing: false };
    }

    render(): React.ReactNode {
        const isEmpty = !this.props.employees.length;
        const noEmployeesClass = isEmpty ? 'nomst-deleted-employees--no-employees' : '';

        return (
            <div className={`nomst-deleted-employees ${noEmployeesClass}`}>
                {!isEmpty && this.renderEmployees()}
                {isEmpty && this.renderNoDeletedEmployees()}
                {this.renderDeleteUserModal()}
            </div>
        );
    }

    renderEmployees(): React.ReactNode {
        const { employees } = this.props;
        const headerMessage = 'The following employees have been deleted from your account,' +
            ' please confirm to remove them from the manager\'s team.';

        return (
            <>
                <div className="nomst-deleted-employees__header">
                    <p className="nomst-deleted-employees__description">
                        {headerMessage}
                    </p>
                    <button
                        className="pxl-btn pxl-btn--primary nomst-deleted-employees__remove-btn"
                        onClick={this.handleOpenDeleteUserModal}
                    >
                        Remove Employees
                    </button>
                </div>
                <div className="nomst-deleted-employees__table" >
                    <div className="nomst-deleted-employees__table-header">
                        <div className="nomst-deleted-employees__employee-count">
                            Employees ({employees.length})
                        </div>
                        <div className="nomst-deleted-employees__reporting-to">
                            Reporting To
                        </div>
                    </div>
                    <ul className="nomst-deleted-employees__table-contents">
                        {employees.map(this.renderEmployeeRow)}
                    </ul>
                </div>
            </>
        );
    }

    renderNoDeletedEmployees(): React.ReactNode {
        const noEmployeesMessage = 'If any gaps are found in your data, we\'ll surface them here' +
            ' to help you resolve the issues quickly.';

        return (
            <>
                <p className="nomst-deleted-employees__no-employees-header">
                    Employees are up-to-date
                </p>
                <p className="nomst-deleted-employees__no-employees-body">
                    {noEmployeesMessage}
                </p>
            </>
        );
    }

    private renderEmployeeRow(employee: IEmployee): React.ReactNode {
        return (
            <li className="nomst-deleted-employees__row" key={employee.employeeSourceId}>
                <div className="nomst-deleted-employees__employee-info">
                    <div className="nomst-deleted-employees__employee-name">
                        {employee.displayName || 'Unknown Employee Name'}
                    </div>
                    <div className="nomst-deleted-employees__employee-job-title">
                        {employee.jobTitleInternal || 'Unknown Job Title'}
                    </div>
                </div>
                <div className="nomst-deleted-employees__manager">
                    {employee.managerDisplayName || 'Unknown Manager Name'}
                </div>
            </li>
        );
    }

    private renderDeleteUserModal(): React.ReactNode {
        if (!this.state.showDeleteModal) {
            return null;
        }

        return (
            <DeleteUserModal
                userCount={this.userCount}
                isProcessing={this.state.isProcessing}
                onConfirmClick={this.handleDeleteEmployeesClick}
                onCancelClick={this.handleDeleteEmployeesKeepUsersClick}
                onCloseClick={this.handleCancelClick}
            />
        );
    }

    private handleOpenDeleteUserModal(): void {
        if (this.userCount) {
            this.setState({ showDeleteModal: true });
        } else {
            this.handleDeleteEmployeesKeepUsersClick();
        }
    }

    private handleCancelClick(): void {
        this.setState({ showDeleteModal: false });
    }

    private async handleDeleteEmployeesClick(): Promise<void> {
        const { employees, deleteEmployeesCall } = this.props;
        this.setState({ isProcessing: true });
        await deleteEmployeesCall(employees, true);
        this.setState({ showDeleteModal: false, isProcessing: false });
    }

    private async handleDeleteEmployeesKeepUsersClick(): Promise<void> {
        const { employees, deleteEmployeesCall } = this.props;
        this.setState({ isProcessing: true });
        await deleteEmployeesCall(employees, false);
        this.setState({ showDeleteModal: false, isProcessing: false });
    }
}